import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTradingOrderPlaceOrder, useTradingPositionListPositions } from '@/lib/endpoints';
import {
  PlaceOrderDtoAction,
  PlaceOrderDtoType,
  PlaeePositionResponseDtoSide,
  PlaeePositionResponseDtoStatus,
  TradingPositionListPositionsStatus,
} from '@/lib/models';
import type { PlaeePositionResponseDto } from '@/lib/models';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import { closeSide } from '@/lib/trading/position';
import { fmtUsd, signedUsd } from '@/lib/trading/format';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import { CollapsibleSection } from '@/components/features/BinaryMarkets/CollapsibleSection';
import { SNAKE_SECTION_ROW, SNAKE_SECTION_HEADER } from './section-row-style';

export interface SnakeOpenPositionsProps {
  /** Outcome to scope the position list to (the market currently being viewed). */
  outcomeId: string;
  /** Live Up price in cents (0–100), or `null` until known — drives live value/P&L. */
  upCents: number | null;
  /** Live Down price in cents (0–100), or `null` until known. */
  downCents: number | null;
}

/**
 * Open Positions section for the snake market page (ARC-84 §5, Figma node 1645-57082).
 *
 * Lists the authenticated customer's open position(s) on the current outcome
 * (netted per party+outcome, so typically one row) from the local mirror.
 * Value and P&L are computed **live** from the Centrifugo price of the held side
 * (`value = qty × live price`, `P&L = value − qty × entry`), matching the binary
 * page — falling back to the wire snapshot only before a live price arrives. Each
 * row exposes a **Sell** that places a market `sell` to close the held quantity,
 * then invalidates the trading mirrors. Gated by the kill-switch + territory gate.
 */
export function SnakeOpenPositions({ outcomeId, upCents, downCents }: SnakeOpenPositionsProps) {
  const queryClient = useQueryClient();
  const territory = useTerritory();
  const openTerritoryGate = useOpenTerritoryGate();
  const ensureOrderAuth = useOnboardingGate('order_gate');
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const [sellingId, setSellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: positions = [] } = useTradingPositionListPositions(
    { outcomeId, status: TradingPositionListPositionsStatus.open },
    { query: { enabled: Boolean(outcomeId) } },
  );

  const open = positions.filter((p) => p.status === PlaeePositionResponseDtoStatus.OPEN && p.quantity > 0);

  // Live value + P&L from the held side's Centrifugo price; entry is the API's
  // avgEntryPrice (Plaee's, or derived from fills). Falls back to the wire
  // snapshot only when no live price has arrived yet.
  function project(p: PlaeePositionResponseDto): { value: number; pnl: number } {
    const isUp = p.side === PlaeePositionResponseDtoSide.LONG;
    const cents = isUp ? upCents : downCents;
    if (cents == null) return { value: p.cost ?? 0, pnl: p.unrealizedPnl ?? 0 };
    const value = (p.quantity * cents) / 100;
    const entry = p.avgEntryPrice;
    const pnl = entry != null && entry > 0 ? value - p.quantity * entry : (p.unrealizedPnl ?? 0);
    return { value, pnl };
  }

  const rows = open.map((p) => ({ position: p, ...project(p) }));
  const totalPnl = rows.reduce((sum, r) => sum + r.pnl, 0);

  const placeOrder = useTradingOrderPlaceOrder({
    mutation: {
      onSuccess: (result) => {
        if (!result.success) {
          setError(ORDER_FAILURE_MESSAGE[toOrderFailureReason(result.errorCode)]);
          return;
        }
        invalidateTradingCaches(queryClient);
      },
      onError: () => setError(ORDER_FAILURE_MESSAGE.network),
      onSettled: () => setSellingId(null),
    },
  });

  function handleSell(p: PlaeePositionResponseDto) {
    if (!tradingEnabled || sellingId) return;
    // Signup-funnel resume comes before any verification gate (ARC-145).
    if (!ensureOrderAuth()) return;
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    setError(null);
    setSellingId(p.positionId);
    placeOrder.mutate({
      data: {
        outcomeId: p.outcomeId,
        side: closeSide(p.side),
        action: PlaceOrderDtoAction.sell,
        count: p.quantity,
        type: PlaceOrderDtoType.market,
      },
    });
  }

  const title = open.length > 0 ? `${open.length} Open Position${open.length === 1 ? '' : 's'}` : 'Open Positions';

  return (
    <CollapsibleSection
      title={title}
      className={SNAKE_SECTION_ROW}
      headerClassName={SNAKE_SECTION_HEADER}
      disabled={open.length === 0}
      summary={
        open.length > 0 ? (
          <span
            className={`rounded-[51px] px-2.5 py-0.5 text-[14px] font-medium tracking-[-0.28px] ${
              totalPnl >= 0 ? 'bg-up/10 text-up' : 'bg-danger/10 text-danger-alt'
            }`}
          >
            {signedUsd(totalPnl)}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2">
        {rows.map(({ position: p, value, pnl }) => (
          <PositionRow
            key={p.positionId}
            position={p}
            value={value}
            pnl={pnl}
            disabled={!tradingEnabled}
            pending={sellingId === p.positionId}
            onSell={() => handleSell(p)}
          />
        ))}
        {error && <p className="text-center text-[13px] font-medium text-danger-alt">{error}</p>}
      </div>
    </CollapsibleSection>
  );
}

/** One open-position row: side + quantity, live value + P&L, and a Sell button. */
function PositionRow({
  position,
  value,
  pnl,
  disabled,
  pending,
  onSell,
}: {
  position: PlaeePositionResponseDto;
  value: number;
  pnl: number;
  disabled: boolean;
  pending: boolean;
  onSell: () => void;
}) {
  const isUp = position.side === PlaeePositionResponseDtoSide.LONG;

  return (
    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-border bg-surface px-3 py-2">
      <div className="flex flex-col">
        <span className={`text-[15px] font-semibold ${isUp ? 'text-up' : 'text-danger-alt'}`}>
          {isUp ? 'Up' : 'Down'} ×{position.quantity}
        </span>
        <span className="text-[12px] text-muted">Value {fmtUsd(value)}</span>
      </div>

      <div className="flex flex-col items-end">
        <span className={`text-[14px] font-semibold tabular-nums ${pnl >= 0 ? 'text-up' : 'text-danger-alt'}`}>
          {signedUsd(pnl)}
        </span>
        <span className="text-[12px] text-muted">P&amp;L</span>
      </div>

      <button
        type="button"
        onClick={onSell}
        disabled={disabled || pending}
        className="h-[34px] rounded-[51px] bg-danger px-4 text-[14px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
      >
        {pending ? 'Selling…' : 'Sell'}
      </button>
    </div>
  );
}
